import cx from 'classnames';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { ExpandableLink } from '@bwoty-web/ui-kit';
import { useIsGlobetrotter } from '@bwoty-web/ui-kit/hooks';

import { scrollUpToElementIfNeeded } from '../../utils/elements';
import InspirationalImages from '../hotelInfo/components/inspirationalImages';

import './PackagePlusMainOptionView.scss';

const PARAGRAPH_BREAK = '\n';
const DESCRIPTION_REST_ID = 'geo-package-plus-main-option__description-rest';
const DESCRIPTION_WRAPPER_CLASS =
  'geo-package-plus-main-option__description-wrapper';

const PackagePlusMainOptionView = ({
  mainOption: { heading, images, text, highlights } = {},
  labels,
  id = undefined,
}) => {
  const isGlobetrotter = useIsGlobetrotter();
  const [isExpanded, setIsExpanded] = useState(false);

  // The preview is cut at the first paragraph break, per design — deliberately
  // not a line clamp like sectionParagraph/cruiseShipDescription.
  const normalizedText = text?.replace(/\r\n/g, '\n') ?? '';
  const breakIndex = normalizedText.indexOf(PARAGRAPH_BREAK);
  const hasBreak = breakIndex !== -1;
  const preview = hasBreak
    ? normalizedText.slice(0, breakIndex)
    : normalizedText;
  // Keeps the leading break, so pre-wrap renders exactly as before once expanded.
  const rest = hasBreak ? normalizedText.slice(breakIndex) : '';

  const handleToggleExpansion = () => {
    setIsExpanded(!isExpanded);
    const [wrapperElement] = document.getElementsByClassName(
      DESCRIPTION_WRAPPER_CLASS,
    );
    if (wrapperElement) {
      scrollUpToElementIfNeeded(wrapperElement);
    }
  };

  return (
    <div
      className={cx({
        'uikit-box': !isGlobetrotter,
      })}
    >
      {heading && (
        <h2 className="geo-package-plus-main-option__heading">{heading}</h2>
      )}
      <div
        className={cx('geo-package-plus-main-option__content', {
          'geo-package-plus-main-option__content--no-highlights': !highlights,
        })}
      >
        {text && (
          <div className={DESCRIPTION_WRAPPER_CLASS}>
            <p
              className={cx('geo-package-plus-main-option__description', {
                'geo-package-plus-main-option__description--collapsed':
                  !isExpanded,
              })}
            >
              {preview}
              {hasBreak && (
                <span
                  id={DESCRIPTION_REST_ID}
                  className="geo-package-plus-main-option__description-rest"
                >
                  {rest}
                </span>
              )}
            </p>
            {hasBreak && (
              <ExpandableLink
                className="geo-package-plus-main-option__expand-link"
                isOpen={isExpanded}
                onClick={handleToggleExpansion}
                aria-controls={DESCRIPTION_REST_ID}
              >
                {isExpanded
                  ? labels?.showLess || 'Show less'
                  : labels?.readMore || 'Read more'}
              </ExpandableLink>
            )}
          </div>
        )}
        {highlights && (
          <div className="geo-package-plus-main-option__highlights">
            <h3 className="geo-package-plus-main-option__highlights-heading">
              {highlights.heading}
            </h3>
            <ul className="geo-package-plus-main-option__highlights-list">
              {highlights.items.map(({ key, value }) => (
                <li
                  key={key}
                  className="geo-package-plus-main-option__highlights-list-item"
                >
                  {/* eslint-disable-next-line react/no-danger */}
                  <span dangerouslySetInnerHTML={{ __html: value }} />
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      {!!images?.length && (
        <div className="geo-package-plus-main-option__images">
          <InspirationalImages
            images={images.slice(1)}
            imagesCount={images.length}
            isConceptHotel={false}
            isGlobe={isGlobetrotter}
            labels={labels}
            id={id}
            itemType="packageplus"
          />
        </div>
      )}
    </div>
  );
};

PackagePlusMainOptionView.propTypes = {
  mainOption: PropTypes.shape({
    heading: PropTypes.string,
    images: PropTypes.arrayOf(PropTypes.shape()),
    text: PropTypes.string,
    highlights: PropTypes.shape({
      heading: PropTypes.string,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.number,
          value: PropTypes.string,
        }),
      ),
    }),
  }),
  labels: PropTypes.shape().isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default PackagePlusMainOptionView;
